import { ComponentProps, useCallback, useEffect, useRef } from 'react';
import { Popover } from 'react-aria-components';
import styled from 'styled-components';
import { colors, rgbaColors } from '@karnott/colors';
import { getColorSeverity, pixelSize, pixelSpacing } from '@karnott/theme';
import { ActionMenuItem, MenuPosition } from '.';

const ItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 7px;
  background-color: white;
  box-shadow: 0px 0px 10px ${rgbaColors('black', 600, 0.1)};
  overflow: hidden;

  &:empty {
    display: none;
  }
`;

const Item = styled.div<{
  $disabled?: boolean;
  $color: string;
}>`
  display: flex;
  align-items: center;
  font-size: ${pixelSize('small')};
  padding: ${pixelSpacing('xSmall')} ${pixelSpacing('small')};
  color: ${({ $color }) => $color};
  cursor: ${({ $disabled = false }) => ($disabled ? 'default' : 'pointer')};
  gap: ${pixelSpacing('small')};
  span {
    white-space: nowrap;
    padding: 2px 0;
  }
  &:hover {
    background-color: ${({ $disabled = false }) => ($disabled ? 'inherit' : colors('grey', 200))};
  }
`;

type ActionMenuPopoverProps = {
  triggerRef: React.RefObject<HTMLElement | null>;
  items: ActionMenuItem[];
  opened: boolean;
  open: () => void;
  close: () => void;
  position: MenuPosition | ComponentProps<typeof Popover>['placement'];
  offset?: number;
};

export function ActionMenuPopover({
  triggerRef,
  items,
  opened,
  open,
  close,
  position,
  offset = 0,
}: ActionMenuPopoverProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  const onItemClick = useCallback(
    (action?: () => void) => {
      action?.();
      close();
    },
    [close],
  );

  const onDocumentClick = useCallback(
    (e: MouseEvent) => {
      if (
        triggerRef.current &&
        e.target instanceof Element &&
        !triggerRef.current.contains(e.target) &&
        menuRef.current &&
        !menuRef.current.contains(e.target)
      ) {
        if (opened) close();
      }
    },
    [close, opened, triggerRef],
  );

  useEffect(() => {
    if (opened) {
      document.addEventListener('click', onDocumentClick, true);
    }
    return () => {
      document.removeEventListener('click', onDocumentClick, true);
    };
  }, [onDocumentClick, opened]);

  return (
    <Popover
      triggerRef={triggerRef}
      isOpen={opened}
      onOpenChange={(isOpen) => (isOpen ? open() : close())}
      offset={offset}
      placement={position === 'right' ? 'right top' : position === 'left' ? 'left top' : position}
      isNonModal
      shouldCloseOnInteractOutside={() => false}
    >
      <ItemsContainer ref={menuRef} data-testid="action-menu-portal">
        {items.map((item) => {
          const itemColor = colors(
            !item.severity || item.severity === 'info' ? 'black' : getColorSeverity(item.severity),
            500,
          );
          return (
            <Item
              key={item.label}
              onClick={(e) => {
                e.stopPropagation();
                onItemClick(item.action);
              }}
              $disabled={!item.action}
              $color={itemColor}
              data-testid="action-menu-item"
            >
              {item.icon ? <item.icon size={12} color={itemColor} /> : null} <span>{item.label}</span>
            </Item>
          );
        })}
      </ItemsContainer>
    </Popover>
  );
}
